import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Calendar, Image as ImageIcon, Mail, Phone, MapPin, Award, Target, Building2, Heart, Send, Star, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { FourStars, StarsDivider } from "../components/FourStars";
import { toast } from "sonner";

const PHOTOS = {
  bureau: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/w32wtzn3_BUREAU%20AFB.JPG",
  banniere2: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/z7yiome8_BANNIERE2.JPG",
  banniere5: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/nplw4r61_BANNIERE%205.JPG",
  banniere3: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3cn3sivo_BANNIERE%203.JPG",
  banniere1: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/scki92mu_BANNIERE1.JPG",
  ministre: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/3p1c0gg5_ABRAHAM%20%2B%20MINISTRES.JPG",
  audience: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/4y1r0u17_ABRA%20%2B%20PRESIDENT.JPG",
  international: "https://customer-assets.emergentagent.com/job_afb-presidentiel/artifacts/0o7lfgvn_ABRAH%20%2B%20SUMOI.JPG",
};

// ============================================================================
// PageHero — réutilisable USN-style
// ============================================================================
function PageHero({ overline, title, subtitle, image }) {
  return (
    <section className="relative py-24 md:py-32 text-white overflow-hidden">
      {image ? (
        <>
          <div className="absolute inset-0" style={{ backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/95 via-[#0A1F44]/80 to-[#0A1F44]/60" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1F44] via-[#0052CC] to-[#0A1F44]" />
      )}
      <div className="absolute inset-0 stars-pattern opacity-15" />
      <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
        <FourStars size={14} className="justify-center mb-4" />
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">{overline}</div>
        <h1 className="font-display text-4xl md:text-6xl mt-3 leading-tight">{title}</h1>
        {subtitle && <p className="text-white/85 mt-6 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">{subtitle}</p>}
        <div className="flex items-center justify-center gap-2 mt-8">
          <span className="w-10 h-1 bg-[#0052CC]" />
          <span className="w-10 h-1 bg-[#D4AF37]" />
          <span className="w-10 h-1 bg-[#CE1126]" />
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// ORGANISATION
// ============================================================================
export function OrganisationPage() {
  const pillars = [
    { icon: Target, title: "Notre mission", text: "Soutenir les actions du Président, mobiliser la diaspora et le peuple, et bâtir un Congo fort, uni et prospère." },
    { icon: Award, title: "Nos valeurs", text: "Unité, Progrès, Travail. Nous croyons en l'excellence, la transparence et l'engagement patriotique." },
    { icon: Building2, title: "Notre structure", text: "Un Bureau exécutif national + sections territoriales (26 provinces RDC) + 6 sections diaspora actives." },
  ];

  return (
    <div data-testid="organisation-page" className="bg-white">
      <PageHero
        overline="Qui sommes-nous"
        title="L'organisation A.F.B."
        subtitle="Un mouvement citoyen patriotique, structuré et engagé au service du peuple congolais."
        image={PHOTOS.bureau}
      />

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Histoire</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#0A1F44] mt-3">Un mouvement né de la conviction</h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              Les Amis de Fatshi Béton (A.F.B.) ont été créés pour rassembler les citoyens congolais et la diaspora autour de la vision du Président de la République Démocratique du Congo.
              Notre engagement est total : promouvoir les actions présidentielles, structurer une mobilisation patriotique, et apporter notre contribution à la construction d'une nation forte.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed">
              Présente dans toutes les provinces de la RDC et dans 6 sections diaspora (Bruxelles, Paris, Londres, Toronto, Johannesburg, Washington), notre organisation grandit chaque jour grâce à l'engagement de ses membres.
            </p>
          </div>
          <img src={PHOTOS.banniere2} alt="A.F.B. en réunion officielle" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
        </div>
      </section>

      <section className="py-20 bg-[#F8F9FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-3xl md:text-4xl text-[#0A1F44]">Mission · Valeurs · Structure</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pillars.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl border border-black/5 shadow-sm p-7"
                data-testid={`org-pillar-${i}`}
              >
                <div className="w-12 h-12 rounded-md bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center mb-4">
                  <p.icon size={22} />
                </div>
                <h3 className="font-display text-xl text-[#0A1F44]">{p.title}</h3>
                <p className="text-sm text-[#475569] mt-3 leading-relaxed">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conseil d'Administration */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Gouvernance</div>
            <h2 className="font-display text-3xl md:text-5xl text-[#0A1F44] mt-3">Conseil d'Administration</h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              Le CA définit les grandes orientations stratégiques du mouvement et veille à la bonne exécution des décisions de l'Assemblée Générale.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { t: "Président du CA", d: "Représente l'association et préside les réunions.", icon: Award },
              { t: "Membres élus", d: "Désignés par l'Assemblée Générale pour 3 ans renouvelables.", icon: Users },
              { t: "Membres de droit", d: "Anciens présidents et membres fondateurs en exercice.", icon: Building2 },
            ].map((c, i) => (
              <div key={i} className="bg-white rounded-2xl border border-black/5 shadow-sm p-7" data-testid={`ca-card-${i}`}>
                <div className="w-12 h-12 rounded-md bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37]">
                  <c.icon size={20} />
                </div>
                <h3 className="font-display text-lg text-[#0A1F44] mt-4">{c.t}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bureau Exécutif */}
      <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#0052CC] text-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Direction quotidienne</div>
            <h2 className="font-display text-3xl md:text-5xl mt-3">Bureau Exécutif</h2>
            <p className="text-white/80 mt-5 leading-relaxed">
              L'instance opérationnelle qui pilote l'action quotidienne du mouvement et coordonne les douze commissions thématiques.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-12">
            {[
              { t: "Président", d: "Direction générale du mouvement" },
              { t: "Vice-présidents", d: "Délégations sectorielles" },
              { t: "Secrétaire général", d: "Coordination administrative" },
              { t: "Trésorier", d: "Gestion financière transparente" },
            ].map((b, i) => (
              <div key={i} className="bg-white/5 backdrop-blur rounded-2xl p-6 border border-white/10 text-center" data-testid={`bureau-card-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37] mx-auto">
                  <Star size={22} fill="#D4AF37" />
                </div>
                <h3 className="font-display text-lg mt-4">{b.t}</h3>
                <p className="text-xs text-white/75 mt-2 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-[#D4AF37] hover:bg-[#B5952F] text-[#0A1F44] rounded-full font-bold uppercase">
              <Link to="/equipe">Découvrir l'équipe complète <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <StarsDivider />

      <section className="py-20 bg-gradient-to-br from-[#0A1F44] to-[#0052CC] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FourStars size={16} className="justify-center mb-5" />
          <h2 className="font-display text-3xl md:text-5xl">Rejoignez-nous</h2>
          <p className="text-white/80 mt-4 leading-relaxed">Devenez membre A.F.B. et participez activement à la transformation de la République Démocratique du Congo.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
            <Button asChild size="lg" className="bg-white hover:bg-white/95 text-[#0A1F44] rounded-full font-bold uppercase">
              <Link to="/register">Devenir membre <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <Button asChild size="lg" className="border-2 border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1F44] rounded-full font-bold uppercase">
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// ÉQUIPE
// ============================================================================
export function TeamPage() {
  const team = [
    { name: "Abraham M.", role: "Coordinateur Général", bio: "Animateur du mouvement, en lien permanent avec les institutions présidentielles.", img: PHOTOS.audience },
    { name: "Bureau Exécutif", role: "Comité de Pilotage", bio: "Une équipe pluridisciplinaire engagée dans la coordination nationale et internationale.", img: PHOTOS.bureau },
    { name: "Délégation Diaspora", role: "Représentants internationaux", bio: "Présence active à Bruxelles, Paris, Londres, Toronto, Johannesburg, Washington.", img: PHOTOS.international },
    { name: "Bureau Communication", role: "Médias & Stratégie", bio: "Mobilisation digitale, relations presse et coordination des campagnes citoyennes.", img: PHOTOS.banniere5 },
    { name: "Bureau Solidarité", role: "Action humanitaire", bio: "Pilotage de la solidarité Est du Congo, éducation et santé.", img: PHOTOS.ministre },
    { name: "Bureau Trésorerie", role: "Gestion financière", bio: "Transparence, rigueur, traçabilité de toutes les contributions.", img: PHOTOS.banniere2 },
  ];

  return (
    <div data-testid="team-page" className="bg-white">
      <PageHero
        overline="Notre Équipe"
        title="Les visages du mouvement"
        subtitle="Une équipe nationale et internationale au service de la République Démocratique du Congo."
        image={PHOTOS.bureau}
      />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all"
                data-testid={`team-card-${i}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={t.img} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">{t.role}</div>
                  <h3 className="font-display text-xl text-[#0A1F44] mt-1.5">{t.name}</h3>
                  <p className="text-sm text-[#475569] mt-2.5 leading-relaxed">{t.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// ÉVÉNEMENTS
// ============================================================================
export function EventsPage() {
  const events = [
    { date: "15 Mars 2026", title: "Grande Assemblée nationale A.F.B.", location: "Kinshasa, Centre Pullman", desc: "Réunion stratégique annuelle du mouvement, ouverte aux membres.", img: PHOTOS.banniere2 },
    { date: "22 Mars 2026", title: "Mobilisation diaspora Bruxelles", location: "Bruxelles, Belgique", desc: "Rencontre publique avec la délégation A.F.B. et la communauté congolaise.", img: PHOTOS.international },
    { date: "5 Avril 2026", title: "Campagne Soutien à l'Est", location: "Paris · Visioconférence", desc: "Levée de fonds et présentation du plan humanitaire 2026.", img: PHOTOS.banniere5 },
    { date: "20 Avril 2026", title: "Forum Jeunesse A.F.B.", location: "Lubumbashi", desc: "Espace d'échange dédié aux jeunes engagés du mouvement.", img: PHOTOS.audience },
  ];
  return (
    <div data-testid="events-page" className="bg-white">
      <PageHero
        overline="Événements"
        title="Notre agenda"
        subtitle="Réunions, rassemblements, conférences et mobilisations à travers la RDC et la diaspora."
        image={PHOTOS.banniere5}
      />
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 space-y-6">
          {events.map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all flex flex-col md:flex-row"
              data-testid={`event-${i}`}
            >
              <div className="md:w-2/5 overflow-hidden">
                <img src={e.img} alt={e.title} className="w-full h-56 md:h-full object-cover" />
              </div>
              <div className="md:w-3/5 p-7 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold">
                  <Calendar size={12} /> {e.date}
                </div>
                <h3 className="font-display text-2xl text-[#0A1F44] mt-2">{e.title}</h3>
                <p className="text-sm text-[#475569] mt-2.5 flex items-center gap-1.5"><MapPin size={13} className="text-[#0052CC]" /> {e.location}</p>
                <p className="text-sm text-[#475569] mt-3 leading-relaxed flex-1">{e.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// MÉDIAS
// ============================================================================
export function MediaPage() {
  const photos = [PHOTOS.audience, PHOTOS.banniere2, PHOTOS.banniere5, PHOTOS.banniere1, PHOTOS.banniere3, PHOTOS.bureau, PHOTOS.ministre, PHOTOS.international];
  return (
    <div data-testid="media-page" className="bg-white">
      <PageHero
        overline="Médias"
        title="Galerie & ressources"
        subtitle="Photos, vidéos et coupures de presse du mouvement A.F.B."
        image={PHOTOS.banniere1}
      />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="aspect-square rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer"
                data-testid={`media-photo-${i}`}
              >
                <img src={p} alt={`A.F.B. — ${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// CONTACT
// ============================================================================
export function ContactPage() {
  const [form, setForm] = React.useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      toast.success("Votre message a bien été reçu. Nous reviendrons vers vous rapidement.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setSending(false);
    }, 800);
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <PageHero
        overline="Contact"
        title="Nous écrire"
        subtitle="Questions, partenariats, presse, mobilisation. L'équipe A.F.B. est à votre écoute."
      />
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-5">
            <div className="bg-[#F8F9FB] rounded-xl p-5">
              <Mail size={18} className="text-[#0052CC]" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-2">Email</div>
              <div className="text-sm font-semibold text-[#0A1F44] mt-1">contact@afb-rdc.cd</div>
            </div>
            <div className="bg-[#F8F9FB] rounded-xl p-5">
              <Phone size={18} className="text-[#0052CC]" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-2">Téléphone</div>
              <div className="text-sm font-semibold text-[#0A1F44] mt-1">+243 000 000 000</div>
            </div>
            <div className="bg-[#F8F9FB] rounded-xl p-5">
              <MapPin size={18} className="text-[#0052CC]" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-2">Adresse</div>
              <div className="text-sm font-semibold text-[#0A1F44] mt-1">Kinshasa, République Démocratique du Congo</div>
            </div>
          </div>

          <form onSubmit={submit} className="md:col-span-2 bg-white rounded-2xl border border-black/5 shadow-sm p-7 space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Nom complet" className="px-4 py-3 rounded-md border border-black/10 focus:border-[#0052CC] focus:outline-none" data-testid="contact-name" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="Email" className="px-4 py-3 rounded-md border border-black/10 focus:border-[#0052CC] focus:outline-none" data-testid="contact-email" />
            </div>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required placeholder="Sujet" className="w-full px-4 py-3 rounded-md border border-black/10 focus:border-[#0052CC] focus:outline-none" data-testid="contact-subject" />
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required placeholder="Votre message..." rows={6} className="w-full px-4 py-3 rounded-md border border-black/10 focus:border-[#0052CC] focus:outline-none" data-testid="contact-message" />
            <Button type="submit" disabled={sending} className="bg-[#0052CC] hover:bg-[#003D99] text-white rounded-full px-8 py-6 font-bold uppercase tracking-wider" data-testid="contact-submit">
              {sending ? "Envoi..." : <>Envoyer <Send size={15} className="ml-2" /></>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}

// ============================================================================
// GUIDES (admin internal docs)
// ============================================================================
export function GuidesPage() {
  const guides = [
    { title: "Statuts & Règlement intérieur", desc: "Documents officiels du mouvement A.F.B." },
    { title: "Charte du membre", desc: "Engagements, droits et devoirs des membres." },
    { title: "Guide des sections territoriales", desc: "Comment animer une section provinciale ou diaspora." },
    { title: "Guide cotisations & dons", desc: "Procédures, transparence et reporting financier." },
    { title: "Guide communication", desc: "Charte de prise de parole publique." },
    { title: "Guide événements", desc: "Organisation des rassemblements, AG et conférences." },
  ];
  return (
    <div data-testid="guides-page" className="bg-white">
      <PageHero overline="Guides internes" title="Documentation des membres" subtitle="Ressources officielles pour les membres A.F.B." />
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {guides.map((g, i) => (
            <div key={i} className="bg-[#F8F9FB] rounded-xl p-6 border border-black/5 hover:border-[#D4AF37]/40 transition-all" data-testid={`guide-${i}`}>
              <Star size={16} fill="#D4AF37" stroke="#D4AF37" />
              <h3 className="font-display text-lg text-[#0A1F44] mt-3">{g.title}</h3>
              <p className="text-sm text-[#475569] mt-2">{g.desc}</p>
              <button className="text-sm text-[#0052CC] font-semibold mt-3 hover:underline">Consulter →</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default OrganisationPage;
